import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Footer } from 'src/shared';
import { UserSelectors } from 'src/reducers/user';
import { getAuthToken, clearAuthToken } from 'src/utils/auth';
import { clearAll } from 'src/services/sessionStorage';
import { clearTermsAgreementStatus, setTermsAgreementStatus } from 'src/utils/termsAgreement';
import { useTermsAgreement } from 'src/hooks/useTermsAgreement';
import TermsAndConditionsModal from 'src/components/TermsAndConditionsModal';
import MFASetupModal from 'src/components/MFASetupModal';
import { verboseLog } from 'src/utils/config';
import { ReactComponent as AdminIcon } from 'src/assets/icons/admin.svg';

const LandingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%);
  color: white;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(30, 58, 138, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 15px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 15px;
  }
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &::before {
    content: '⚕';
    font-size: 28px;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    font-size: 20px;
    gap: 8px;
    
    &::before {
      font-size: 24px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    gap: 6px;
    
    &::before {
      font-size: 20px;
    }
  }
`;

const NavButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.25);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 10px 18px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 13px;
  }
`;

const UserDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    gap: 6px;
  }
`;

const UserName = styled.span`
  color: white;
  font-weight: 600;
`;

const AdminButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: rgba(245, 158, 11, 0.85);
  border: 2px solid rgba(245, 158, 11, 0.6);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(245, 158, 11, 1);
    border-color: rgba(245, 158, 11, 0.8);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const LogoutButton = styled.button`
  background: rgba(239, 68, 68, 0.8);
  border: 2px solid rgba(239, 68, 68, 0.6);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 1);
    border-color: rgba(239, 68, 68, 0.8);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const HeroSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  padding: 60px 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 30px 15px;
  }
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 30px;
  line-height: 1.2;
  text-shadow: 0 4px 8px rgba(30, 58, 138, 0.4);
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 20px;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 40px;
  opacity: 0.9;
  max-width: 800px;
  line-height: 1.6;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 25px;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 50px;
  max-width: 900px;
  opacity: 0.95;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  color: white;
  padding: 18px 36px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(37, 99, 235, 0.6);
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  }
  
  @media (max-width: 768px) {
    padding: 16px 32px;
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 14px 28px;
    font-size: 15px;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 40px;
  background: rgba(30, 58, 138, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 15px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureCard = styled.div<{ clickable?: boolean }>`
  background: rgba(255, 255, 255, 0.08);
  padding: 30px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  
  &:hover {
    transform: ${props => props.clickable ? 'translateY(-5px)' : 'none'};
    background: ${props => props.clickable ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${props => props.clickable ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.3)'};
    box-shadow: ${props => props.clickable ? '0 8px 25px rgba(30, 58, 138, 0.2)' : 'none'};
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const CircularFeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  padding: 40px 30px;
  border-radius: 50%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 200px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(59, 130, 246, 0.5);
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(30, 58, 138, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    min-height: 150px;
  }
  
  @media (max-width: 480px) {
    padding: 25px 15px;
    min-height: 120px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
`;



const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isAllowedAdmin } = UserSelectors();
  const isLoggedIn = getAuthToken() && profile;
  const { hasAgreed, refreshAgreementStatus } = useTermsAgreement();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);

  // Check if MFA setup is required when component mounts or when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      const mfaSetupRequired = sessionStorage.getItem('mfa_setup_required') === 'true';
      if (mfaSetupRequired) {
        verboseLog('⚠️ MFA setup required - showing setup modal');
        setShowMFASetup(true);
      }
    }
  }, [isLoggedIn]);

  const handleExploreClick = () => {
    navigate('/gene');
  };

  const handleDrugDiscoveryClick = () => {
    navigate('/drugs');
  };

  const handleMedicalImagingClick = () => {
    navigate('/medical_imaging');
  };

  const handleLoginClick = () => {
    verboseLog('🔵 Login button clicked, navigating to /login');
    navigate('/login');
  };

  const handleSignUpClick = () => {
    navigate('/signup');
  };

  const handleLogoutClick = () => {
    clearAuthToken();
    clearAll();
    clearTermsAgreementStatus();
    navigate('/');
  };

  const handleTermsClick = () => {
    setShowTermsModal(true);
  };

  const handleAgreeToTerms = () => {
    verboseLog('✅ User agreed to terms on landing page');
    setShowTermsModal(false);
    setTermsAgreementStatus(true); // Actually set the agreement status
    verboseLog('📝 Terms agreement status set in session storage');
    refreshAgreementStatus();
    verboseLog('🔄 Agreement status refreshed, navigating to signup...');
    // Navigate to signup page after agreeing to terms
    navigate('/signup');
  };

  const handleMFASetupComplete = () => {
    // Clear the MFA setup required flag
    sessionStorage.removeItem('mfa_setup_required');
    setShowMFASetup(false);
    verboseLog('✅ MFA setup completed - user can now use the system');
    // Refresh the page to reload user profile with updated MFA status
    window.location.reload();
  };

  return (
    <LandingContainer>
      <Header>
        <Logo>Joe Xing IP</Logo>
        <div style={{ display: 'flex', gap: '12px' }}>
          {isLoggedIn ? (
            <UserDisplay>
              <UserName>
                Welcome, {profile?.first_name} {profile?.last_name}
              </UserName>
              {isAllowedAdmin === true && (
                <AdminButton type="button" onClick={() => navigate('/admin/users')} title="Admin – Users & Health">
                  <AdminIcon style={{ width: 18, height: 18, marginRight: 6, verticalAlign: 'middle' }} />
                  Admin
                </AdminButton>
              )}
              <LogoutButton onClick={handleLogoutClick}>
                Logout
              </LogoutButton>
            </UserDisplay>
          ) : (
            <>
              <NavButton type="button" onClick={handleLoginClick}>
                Login
              </NavButton>
                             <NavButton 
                 onClick={hasAgreed ? handleSignUpClick : handleTermsClick}
                 style={{
                   opacity: hasAgreed ? 1 : 0.5,
                   cursor: hasAgreed ? 'pointer' : 'not-allowed',
                   background: hasAgreed ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                   borderColor: hasAgreed ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)'
                 }}
                 title={hasAgreed ? 'Sign Up' : 'Click to view Terms and Conditions'}
               >
                 Sign Up
               </NavButton>
            </>
          )}
        </div>
      </Header>

      <HeroSection>
        <MainTitle>
         Agentic AI That Thinks in Healthcare
        </MainTitle>
        
        <Subtitle>
         Healthcare professionals define goals, and agentic AI creates precise models without programming
        </Subtitle>
        
        <Description>
         We build an agentic AI that reasons across biomedical text, genetic information, 3D molecular structures, and medical imaging. By connecting drugs, diseases, and patient biology, it uncovers meaningful relationships and delivers transparent, evidence-backed insights that help clinicians and researchers make sense of complex data faster and with greater confidence
        </Description>
        
        <CTAButton onClick={handleExploreClick}>
          Explore the Platform
        </CTAButton>
      </HeroSection>

      <FeaturesSection>
        {/* First Row - Primary Healthcare Workflow (2 cards) */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)', 
          gap: window.innerWidth <= 768 ? '20px' : '40px', 
          maxWidth: '1200px', 
          margin: '0 auto 40px auto' 
        }}>
          <FeatureCard clickable={true} onClick={handleMedicalImagingClick}>
            <FeatureTitle>🔬 Multi-modality Medical Imaging</FeatureTitle>
            <FeatureText>
              Medical devices, imaging, surgical robotics technologies that operate at the macroscopic scale.
            </FeatureText>
          </FeatureCard>
          
          <FeatureCard clickable={true} onClick={handleDrugDiscoveryClick}>
            <FeatureTitle>💊 Drug Discovery and Molecular Therapeutics</FeatureTitle>
            <FeatureText>
              3D protein structures, genetic variation, and transomics design at the microscopic scale.
            </FeatureText>
          </FeatureCard>
        </div>

        {/* Second Row - Supporting Healthcare Entities (3 cards) */}
        <FeaturesGrid>
          <CircularFeatureCard>
            <FeatureTitle>🦠 Disease</FeatureTitle>
            <FeatureText>
              AI assists in analyzing medical imaging patterns and data correlations. Doctors make the final diagnosis - AI provides evidence-backed insights to support clinical decision-making.
            </FeatureText>
          </CircularFeatureCard>
          
          <CircularFeatureCard>
            <FeatureTitle>🧬 Proteins & Therapeutic Targets</FeatureTitle>
            <FeatureText>
              DNA, RNA, and protein analysis to identify therapeutic targets. AI connects genetic information with disease mechanisms and drug interactions.
            </FeatureText>
          </CircularFeatureCard>
          
          <CircularFeatureCard>
            <FeatureTitle>⚛️ 3D Molecular Structures</FeatureTitle>
            <FeatureText>
              Advanced molecular modeling and structural analysis. AI helps understand how drugs interact with biological targets at the atomic level.
            </FeatureText>
          </CircularFeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

             <Footer />
       
       <TermsAndConditionsModal
         show={showTermsModal}
         onHide={() => setShowTermsModal(false)}
         onAgree={handleAgreeToTerms}
       />

       {/* MFA Setup Modal - Shown when user logs in but hasn't set up MFA yet */}
       <MFASetupModal
         show={showMFASetup}
         onHide={() => {
           setShowMFASetup(false);
           // If user cancels MFA setup, log them out
           clearAuthToken();
           clearAll();
           sessionStorage.removeItem('mfa_setup_required');
           navigate('/login');
         }}
         onComplete={handleMFASetupComplete}
       />
     </LandingContainer>
   );
 };
 
 export default Landing;